import { supabase } from "../supabase/SupabaseClient";

export { genteParaCompartir, miMail, misPlanillas } from "./espacio";

/* Automatizaciones: lo que el hub lee y escribe. El que CORRE es el bot.
 *
 * Igna, 03/10/2026: «que todos puedan verlo». La RLS lo dice así y acá no se
 * filtra nada a mano: todos LEEN todas las automatizaciones; crear también;
 * cambiar, apagar y borrar, sólo el dueño (la base lo exige, la pantalla sólo
 * esconde los botones). El contrato del jsonb está en
 * `supabase/migrations/20261003040000_automatizaciones.sql` y su armado en
 * `shared/automatizaciones.js`.
 *
 * Las CORRIDAS, en cambio, traen datos: el `detalle` guarda los valores del
 * rango vigilado, los cambios («B3: 100 → 120»), lo que leyó cada paso y el
 * texto que salió. Eso lo lee sólo el dueño
 * (`20261005060000_automatizaciones_valores_privados.sql`). Para todos queda
 * la vista `automatizacion_corridas_publicas`: que corrió, cuándo y cómo le
 * fue, sin `detalle`, `error` ni `origen`.
 */

const COLUMNAS = "id, nombre, descripcion, dueno, activa, disparador, pasos, creado_en, actualizado_en";

export async function listarAutomatizaciones() {
  const { data, error } = await supabase
    .from("automatizaciones")
    .select(COLUMNAS)
    .order("actualizado_en", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function traerAutomatizacion(id) {
  const { data, error } = await supabase
    .from("automatizaciones")
    .select(COLUMNAS)
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

/* `dueno` lo pone la base (default `mi_mail()`); `activa` lo decide quien
   llama: las nuevas del editor nacen apagadas, el aviso de FiboSheets
   prendido. */
export async function crearAutomatizacion({ nombre, descripcion = null, disparador, pasos, activa = false }) {
  const { data, error } = await supabase
    .from("automatizaciones")
    .insert({ nombre, descripcion, disparador, pasos, activa })
    .select(COLUMNAS)
    .single();
  if (error) throw error;
  return data;
}

export async function guardarAutomatizacion(id, { nombre, descripcion = null, disparador, pasos }) {
  const { data, error } = await supabase
    .from("automatizaciones")
    .update({ nombre, descripcion, disparador, pasos, actualizado_en: new Date().toISOString() })
    .eq("id", id)
    .select(COLUMNAS)
    .single();
  if (error) throw error;
  return data;
}

export async function prenderApagar(id, activa) {
  const { data, error } = await supabase
    .from("automatizaciones")
    .update({ activa, actualizado_en: new Date().toISOString() })
    .eq("id", id)
    .select(COLUMNAS)
    .single();
  if (error) throw error;
  return data;
}

export async function borrarAutomatizacion(id) {
  const { error } = await supabase.from("automatizaciones").delete().eq("id", id);
  if (error) throw error;
}

/* ── Corridas ──────────────────────────────────────────────────────────── */

const COLUMNAS_CORRIDA =
  "id, automatizacion_id, tipo, estado, origen, pedida_en, empezada_en, terminada_en, pedida_por, detalle, error";

/* Lo que cualquiera puede ver de una corrida (la vista no tiene más). */
const VISTA_PUBLICA = "automatizacion_corridas_publicas";
const COLUMNAS_PUBLICAS = "id, automatizacion_id, tipo, estado, pedida_en, empezada_en, terminada_en, pedida_por";

/* Las corridas recientes de varias automatizaciones, para la «última corrida»
   de cada tarjeta. De la vista pública: la lista mezcla las mías con las de
   otros y de las de otros la tabla no me devuelve nada; la tarjeta diría
   «Nunca corrió» de una que corre todos los días. */
export async function corridasRecientes(ids) {
  if (!ids || !ids.length) return [];
  const { data, error } = await supabase
    .from(VISTA_PUBLICA)
    .select(COLUMNAS_PUBLICAS)
    .in("automatizacion_id", ids)
    .order("pedida_en", { ascending: false })
    .limit(Math.max(200, ids.length * 5));
  if (error) throw error;
  return data || [];
}

/* El historial completo, con `detalle` y `error`. Sólo para el dueño: a
   cualquier otro la RLS le devuelve cero filas, y eso dibujado diría «nunca
   corrió». Para la de otro, `corridasPublicasDe`. */
export async function corridasDe(id, limite = 30) {
  const { data, error } = await supabase
    .from("automatizacion_corridas")
    .select(COLUMNAS_CORRIDA)
    .eq("automatizacion_id", id)
    .order("pedida_en", { ascending: false })
    .limit(limite);
  if (error) throw error;
  return data || [];
}

/* El historial de una automatización ajena: cuándo corrió y cómo le fue, sin
   lo que leyó ni lo que mandó («el detalle lo ve quien la armó»). */
export async function corridasPublicasDe(id, limite = 30) {
  const { data, error } = await supabase
    .from(VISTA_PUBLICA)
    .select(COLUMNAS_PUBLICAS)
    .eq("automatizacion_id", id)
    .order("pedida_en", { ascending: false })
    .limit(limite);
  if (error) throw error;
  return data || [];
}

export async function traerCorrida(id) {
  const { data, error } = await supabase
    .from("automatizacion_corridas")
    .select(COLUMNAS_CORRIDA)
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

/* Pedir una prueba es insertar ESTA fila y ninguna otra: la RLS deja
   exactamente `tipo='prueba'`, `estado='pedida'`, `pedida_por = mi mail`, y
   sólo sobre una automatización propia. El bot la toma, corre sin escribir ni
   mandar nada, y deja en `detalle` lo que habría hecho. */
export async function pedirPrueba(automatizacionId, yo) {
  const { data, error } = await supabase
    .from("automatizacion_corridas")
    .insert({ automatizacion_id: automatizacionId, tipo: "prueba", estado: "pedida", pedida_por: yo })
    .select(COLUMNAS_CORRIDA)
    .single();
  if (error) throw error;
  return data;
}

/* Seguir una corrida hasta que el bot la termine: realtime (la tabla está en
   la publicación `supabase_realtime`, y el realtime aplica la RLS: le llega
   al dueño, que es el único que pide pruebas) y, POR LAS DUDAS, una consulta cada
   pocos segundos. El realtime se cae sin avisar —una pestaña dormida, un
   proxy— y una prueba que se queda en «esperando» para siempre se lee como
   que el bot no anda. Las dos vías llaman a `alCambiar` con la fila entera;
   devuelve con qué dejar de mirar. */
let canales = 0;
export function seguirCorrida(id, alCambiar, { cadaMs = 4000 } = {}) {
  let vivo = true;
  const avisar = (fila) => { if (vivo && fila) alCambiar(fila); };
  const canal = supabase
    .channel(`corrida:${id}:${(canales += 1)}`)
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "automatizacion_corridas", filter: `id=eq.${id}` },
      (payload) => avisar(payload.new),
    )
    .subscribe();
  const t = setInterval(() => {
    traerCorrida(id).then(avisar).catch(() => {});
  }, cadaMs);
  return () => {
    vivo = false;
    clearInterval(t);
    supabase.removeChannel(canal);
  };
}

/* ── Para FiboSheets ───────────────────────────────────────────────────── */

/* Mis avisos prendidos sobre una planilla: lo que pinta la campanita en las
   celdas. Sólo los MÍOS —la campanita dice «a vos te avisan»—, aunque la
   lista de automatizaciones la vea todo el mundo. */
export async function misAvisosDePlanilla(planillaId, yo) {
  const { data, error } = await supabase
    .from("automatizaciones")
    .select(COLUMNAS)
    .eq("dueno", yo)
    .eq("activa", true)
    .eq("disparador->>tipo", "planilla_cambia")
    .eq("disparador->>planilla_id", planillaId);
  if (error) throw error;
  return data || [];
}

/* Los nombres de las hojas de una planilla, para elegir en el editor. Una
   planilla vieja (un solo grid, sin `hojas`) es «Hoja 1», como la abre
   Mi espacio. */
export async function hojasDePlanilla(id) {
  const { data, error } = await supabase
    .from("espacio_planillas")
    .select("datos")
    .eq("id", id)
    .single();
  if (error) throw error;
  const d = (data && data.datos) || {};
  if (Array.isArray(d.hojas) && d.hojas.length) {
    return d.hojas.map((h, i) => (h && h.nombre) || `Hoja ${i + 1}`);
  }
  return ["Hoja 1"];
}
